/** Rótulo genérico do SO: a bandeja é superfície pública (tela de bloqueio). */
const GENERIC_TITLE = 'Nova mensagem'
/** Preserva o texto que a lista de chats já usava quando não há nome nem telefone. */
const UNKNOWN_CONTACT_LABEL = 'Novo contato'
/** Dígitos suficientes pra que a sequência seja um telefone, e não "Loja 24h". */
const MIN_PHONE_DIGITS = 7

export interface ChatNotificationContact {
  name?: string | null
  phone_number?: string | null
}

export interface ChatNotificationTitles {
  /** Vai pra bandeja do SO **e** é o default do toast. Nunca contém telefone. */
  system: string
  /** Só o toast in-app, superfície privada: pode identificar por telefone. */
  inApp: string
}

/**
 * `name` que na verdade é o telefone. O back preenche
 * `fallback_name = phone_number or user_id` quando o perfil não tem nome
 * (`SocketEvents.ts:186`, `channelActivity.md` Edge cases), e a formatação nem
 * sempre bate com a de `phone_number` — por isso é heurística de FORMA, não
 * comparação com o telefone do contato.
 */
function looksLikePhone(value: string): boolean {
  if (!/^[+(\d][\d\s().+-]*$/.test(value)) return false
  return value.replace(/\D/g, '').length >= MIN_PHONE_DIGITS
}

/**
 * O rótulo do contato que PODE atravessar para a bandeja do SO, ou `null`.
 *
 * `null` cobre os dois casos em que não há nome utilizável: campo ausente/vazio
 * e o `fallback_name = phone_number` do back. Existe como função exportada
 * porque a heurística de FORMA (`looksLikePhone`) é a parte cara desta regra e
 * ela vale para toda frase que vá para a bandeja — não só para o título de
 * `chatNotificationTitle`. O aviso de `channel_activity` monta uma frase de
 * outra forma (o nome fica no MEIO: "maria transferiu X para Vendas") e precisa
 * do mesmo julgamento sem repetir a expressão regular.
 *
 * O telefone do contato **não** entra no lugar: a razão de sanitizar é
 * justamente que a bandeja é a tela de bloqueio.
 */
export function safeContactName(
  contact: ChatNotificationContact | null | undefined,
): string | null {
  const nome = contact?.name?.trim() ?? ''
  if (nome === '' || looksLikePhone(nome)) return null
  return nome
}

/**
 * Título do aviso de mensagem nova, por superfície.
 *
 * **Decisão do usuário (R1, 2026-09-04):** desde que a notificação passou a
 * funcionar de verdade no celular, o título aparece na tela de bloqueio do
 * aparelho. Contato COM nome mostra o nome; contato SEM nome cai no rótulo
 * genérico. **O telefone nunca vai para a bandeja do SO.** Foi esse risco que
 * manteve a API do SO fora do escopo do `channel_activity` até 2026-09-04; hoje
 * aquele aviso também notifica o SO, e é justamente `safeContactName` que o
 * torna seguro (ver [[channelActivity]]). O corpo (prévia da mensagem) é outra
 * regra: quem reduz é o `contentLevel` do funil ([[dispatch]]), não este módulo.
 *
 * O toast in-app não tem essa restrição — quem o vê já está autenticado na tela.
 */
export function chatNotificationTitle(
  contact: ChatNotificationContact | null | undefined,
): ChatNotificationTitles {
  const nome = contact?.name?.trim() ?? ''
  const telefone = contact?.phone_number?.trim() ?? ''
  const seguro = safeContactName(contact)

  const rotuloInApp = seguro ?? nome

  return {
    system: seguro ? `${GENERIC_TITLE} de ${seguro}` : GENERIC_TITLE,
    inApp: `${GENERIC_TITLE} de ${rotuloInApp || telefone || UNKNOWN_CONTACT_LABEL}`,
  }
}
